import { Match2Reward } from '../rewards/match2-rewards.ruleset';

export type Match2TournamentPlayer = {
  score: number;
  scoreUpdatedAt: number;
  id?: string;
  name?: string;
  avatar?: string;
};

export type TournamentType = 'star' | 'streak';

export type WeeklySchedule = {
  from: string;
  to: string;
  type: TournamentType;
}[];

/** Maximum number of players in a single tournament */
const maxPlayers = 50;

/** Feature unlocks at level 25 */
const unlockLevel = 25;

/** Feature unlocks at level 25 */
export const match2TournamentUnlockLevel = 25;

export const maxTournamentsAgeDays = 30;

/** Match2 tournament rewards per finish position */
const tournamentRewards: Record<number, Match2Reward> = {
  1: {
    type: 'gems',
    amount: 2000,
  },
  2: {
    type: 'gems',
    amount: 1000,
  },
  3: {
    type: 'gems',
    amount: 500,
  },
  4: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  5: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  6: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  7: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  8: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  9: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
  10: {
    type: 'boosters',
    subType: 'dart',
    amount: 3,
  },
};

/**
 * Weekely start and end of each tournament, in JST timezone
 */
const weeklySchedule: WeeklySchedule = [
  {
    from: 'Sun Jul 02 2023 23:00:00 GMT+9',
    to: 'Tue Jul 04 2023 22:59:00 GMT+9',
    type: 'star',
  },
  {
    from: 'Tue Jul 04 2023 23:00:00 GMT+9',
    to: 'Thu Jul 06 2023 22:59:00 GMT+9',
    type: 'star',
  },
  {
    from: 'Thu Jul 06 2023 23:00:00 GMT+9',
    to: 'Sun Jul 09 2023 22:59:00 GMT+9',
    type: 'star',
  },
];

const weeklyScheduleV2: WeeklySchedule = [
  {
    from: 'Sun Jul 02 2023 23:00:00 GMT+9',
    to: 'Tue Jul 04 2023 22:59:00 GMT+9',
    type: 'streak',
  },
  {
    from: 'Tue Jul 04 2023 23:00:00 GMT+9',
    to: 'Thu Jul 06 2023 22:59:00 GMT+9',
    type: 'streak',
  },
  {
    from: 'Thu Jul 06 2023 23:00:00 GMT+9',
    to: 'Sun Jul 09 2023 22:59:00 GMT+9',
    type: 'streak',
  },
];

const tournamentMultiplier = [1, 2, 4, 6, 10];

function getRewardsByRank(rank: number) {
  return tournamentRewards?.[rank] ?? null;
}

export const flyingHighUnlockLevel = 100;

type ScoringPattern = {
  min3: number;
  min8: number;
  min15: number;
  min30: number;
  min55: number;
  hourly: number;
  hourlyProbability: number;
};

type ScoringPatterns = {
  slowest: ScoringPattern;
  slow: ScoringPattern;
  normal: ScoringPattern;
  fast: ScoringPattern;
};

const scoringPatterns: ScoringPatterns[] = [
  {
    slowest: {
      min3: 0,
      min8: 0,
      min15: 0,
      min30: 0,
      min55: 0,
      hourly: 1,
      hourlyProbability: 0.1,
    },
    slow: {
      min3: 0,
      min8: 0,
      min15: 0,
      min30: 1,
      min55: 1,
      hourly: 1,
      hourlyProbability: 0.25,
    },
    normal: {
      min3: 1,
      min8: 1,
      min15: 1,
      min30: 0,
      min55: 0,
      hourly: 1,
      hourlyProbability: 0.4,
    },
    fast: {
      min3: 1,
      min8: 3,
      min15: 2,
      min30: 3,
      min55: 2,
      hourly: 2,
      hourlyProbability: 0.7,
    },
  },
  {
    slowest: {
      min3: 0,
      min8: 0,
      min15: 1,
      min30: 0,
      min55: 0,
      hourly: 1,
      hourlyProbability: 0.1,
    },
    slow: {
      min3: 1,
      min8: 0,
      min15: 0,
      min30: 1,
      min55: 0,
      hourly: 1,
      hourlyProbability: 0.2,
    },
    normal: {
      min3: 1,
      min8: 0,
      min15: 1,
      min30: 0,
      min55: 1,
      hourly: 1,
      hourlyProbability: 0.3,
    },
    fast: {
      min3: 1,
      min8: 2,
      min15: 2,
      min30: 3,
      min55: 2,
      hourly: 3,
      hourlyProbability: 0.8,
    },
  },
];

// [slowest, slow, normal, fast]
const playerTypeProbability = {
  v2: [50, 40, 10, 0],
};

const scoringFactor = {
  star: 1,
  streak: 4,
};

export const match2TournamentRuleset = {
  maxPlayers,
  unlockLevel,
  weeklySchedule,
  getRewardsByRank,
  weeklyScheduleV2,
  tournamentMultiplier,
  flyingHighUnlockLevel,
  scoringPatterns,
  scoringFactor,
  playerTypeProbability,
};
